/**
 * Canonical image-type constants shared by the upload UI and the server-side
 * normalizer.
 *
 * This module is intentionally dependency-free (no `heic-convert`, no `node:*`)
 * so it is safe to import from BOTH client components (for `accept` attributes
 * + lightweight pre-checks) AND server routes (for allow-list logic). The
 * heavy lifting — magic-byte sniffing and HEIC→JPEG transcoding — lives in the
 * server-only `lib/images/normalize-upload.ts`.
 *
 * Why this exists: browsers (especially iOS Safari) hand us HEIC/HEIF photos
 * that desktop browsers cannot render, and frequently report an empty or wrong
 * `File.type`. The pipeline therefore (a) accepts a broad set of image types,
 * and (b) transcodes the non-web-renderable ones to JPEG server-side so every
 * uploaded photo displays everywhere.
 */

/** Image formats every modern browser renders natively — stored as-is. */
export const WEB_SAFE_IMAGE_MIME = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/avif",
] as const;

/** Formats we accept but must transcode to JPEG before storing. */
export const CONVERTIBLE_IMAGE_MIME = ["image/heic", "image/heif"] as const;

/** Every image content-type the upload pipeline accepts as input. */
export const ACCEPTED_IMAGE_MIME = [
  ...WEB_SAFE_IMAGE_MIME,
  ...CONVERTIBLE_IMAGE_MIME,
] as const;

export type WebSafeImageMime = (typeof WEB_SAFE_IMAGE_MIME)[number];
export type AcceptedImageMime = (typeof ACCEPTED_IMAGE_MIME)[number];

/** Canonical file extension (no leading dot) for each web-safe stored type. */
export const WEB_SAFE_EXTENSION: Record<WebSafeImageMime, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/avif": "avif",
};

/**
 * Value for an `<input type="file">` `accept` attribute covering every
 * supported photo format. Lists both MIME types AND file extensions on
 * purpose — several OS file pickers only match HEIC/HEIF by extension (the
 * MIME is reported blank), so both forms are required for the file to be
 * selectable. Use this everywhere a site photo can be uploaded.
 */
export const IMAGE_UPLOAD_ACCEPT = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/avif",
  "image/heic",
  "image/heif",
  ".jpg",
  ".jpeg",
  ".png",
  ".webp",
  ".gif",
  ".avif",
  ".heic",
  ".heif",
].join(",");

/**
 * Ceiling on a single photo upload, for the client's `AbortSignal.timeout`.
 *
 * Every photo picker MUST bound its upload request. A photo upload is the one
 * request that can legitimately run for minutes (a 25 MB file over site 4G,
 * plus a server-side HEIC transcode), so an unbounded `fetch` that never
 * settles — a dropped connection, a proxy holding the socket, an instance
 * restarting mid-request — leaves the button spinning forever with nothing to
 * click and no error to read. That is exactly how a HEIC upload presented in
 * production. Three minutes is well clear of a slow-but-real upload while still
 * guaranteeing the spinner always resolves into either a photo or a message.
 */
export const IMAGE_UPLOAD_TIMEOUT_MS = 180_000;

/** Message shown when an upload is aborted by `IMAGE_UPLOAD_TIMEOUT_MS`. */
export const IMAGE_UPLOAD_TIMEOUT_MESSAGE =
  "Upload timed out. Check your connection and try again — large photos take longer.";

const IMAGE_EXTENSION_RE = /\.(jpe?g|png|webp|gif|avif|heic|heif)$/i;

/**
 * Lightweight client-side guard: is this `File` plausibly one of our accepted
 * image types? Use to give immediate feedback BEFORE upload, without
 * false-rejecting HEIC/HEIF (which often arrive with an empty MIME — in that
 * case we fall back to the file extension). The server still does the
 * authoritative magic-byte check, so this only needs to be permissive.
 */
export function isLikelyImageUpload(file: {
  type?: string | null;
  name?: string | null;
}): boolean {
  const type = (file.type ?? "").toLowerCase();
  if (type.startsWith("image/")) return true;
  // Browsers/OSes sometimes report no MIME (or a generic one) for HEIC/HEIF
  // and even the odd JPEG from certain cameras — fall back to the extension.
  if (type === "" || type === "application/octet-stream") {
    return IMAGE_EXTENSION_RE.test(file.name ?? "");
  }
  return false;
}
